import { Slider } from 'antd';
import { useTranslations } from 'next-intl';
import { FC, useEffect, useMemo, useState } from 'react';
import { MonetizationTariff, ProductType } from 'types/enums';
import { getCountedItemKey } from 'utils/getCountedItemKey';
import baseStyles from '../style.module.scss';
import { TariffBaseProps } from '../types';
import styles from './style.module.scss';

type Props = Pick<
  TariffBaseProps<MonetizationTariff.Standard>,
  'items' | 'setStandardProduct' | 'standardProduct' | 'step' | 'useUncontrolled'
>;

const StandardTariff: FC<Props> = ({
  items,
  standardProduct,
  setStandardProduct,
  useUncontrolled,
  step = 2,
}) => {
  const t = useTranslations();
  const countedKey = getCountedItemKey(standardProduct?.contents?.type);

  const standardMarks: Record<string, number> = useMemo(() => {
    return Array.from(
      new Set(
        items
          .filter(
            (item) =>
              item.status.toLowerCase() === 'active' &&
              item.contents.tariff === MonetizationTariff.Standard,
          )
          .map((item) => item.contents.totalCount)
          .sort(),
      ).keys(),
    ).reduce<Record<string, number>>((acc, cur) => {
      acc[cur] = cur;
      return acc;
    }, {});
  }, [items]);

  const standardProducts = items
    .filter(
      (item) =>
        item.status.toLowerCase() === 'active' &&
        item.contents.tariff === MonetizationTariff.Standard,
    )
    .sort((a, b) => a.contents.totalCount - b.contents.totalCount);

  const [valueStandard, setValueStandard] = useState(Object.values(standardMarks)[0]);

  useEffect(() => {
    setValueStandard(Object.values(standardMarks)[0]);
  }, [standardMarks]);

  useEffect(() => {
    const index = Object.keys(standardMarks).findIndex((value) => value === String(valueStandard));
    setStandardProduct(standardProducts[index]);
  }, [valueStandard]);

  if (!valueStandard || !standardProduct) {
    return <span>loading</span>;
  }

  return (
    <>
      <p className={styles.placements}>
        <span className={styles.number}>
          {t(countedKey, { count: valueStandard }).split(' ')[0]}
        </span>{' '}
        {t(countedKey, { count: valueStandard }).split(' ')[1]}
      </p>
      <div className={styles.prices}>
        <span className={baseStyles.price}>
          {standardProduct.contents.meta.currencySymbol}
          {standardProduct.contents.price}
        </span>
        <span className={baseStyles.oldPrice}>
          {standardProduct.contents.meta.currencySymbol}
          {standardProduct.contents.meta.oldPrice}
        </span>
        <span className={baseStyles.badge}>
          -
          {standardProduct.contents.meta.discount ??
            ((standardProduct.contents.meta.oldPrice - standardProduct.contents.price) /
              standardProduct.contents.meta.oldPrice) *
              100}
          %
        </span>
        <div className={baseStyles.perVacancy}>
          {standardProduct.contents.meta.currencySymbol}
          {t(
            standardProduct?.contents?.type === ProductType.VACANCY
              ? 'price_for_vacancies'
              : 'price_for_opening',
            {
              count: Math.floor(
                standardProduct.contents.price / standardProduct.contents.totalCount,
              ),
            },
          )}
        </div>
      </div>
      <Slider
        tooltip={{
          open: false,
        }}
        styles={{
          rail: {
            backgroundColor: '#A2ACB0',
            height: '3px',
          },
          track: {
            backgroundColor: '#007AFF',
            height: '3px',
          },
          handle: {
            width: '2px',
            height: '2px',
          },
        }}
        className={styles.slider}
        min={Object.values(standardMarks)[0]}
        max={Object.values(standardMarks).at(-1)}
        step={step}
        marks={standardMarks}
        value={useUncontrolled ? undefined : valueStandard}
        onChange={setValueStandard}
      />
    </>
  );
};

export default StandardTariff;
